import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Business as BrandIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Article as ArticleIcon,
  LocalOffer as TagIcon,
  FolderSpecial as CollectionIcon,
  TrendingUp as TierIcon,
  Category as CategoryIcon,
  Settings as OverrideIcon,
  Language as DomainIcon,
  Person as UserIcon,
  Email as EmailIcon,
  Group as GroupIcon,
  Warning as WarningIcon,
  AutoAwesome as IAIcon
} from '@mui/icons-material';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';
import api from '../../../../services/api';

const TIER_PROFILES = [
  {
    value: 'small',
    label: 'Pequena',
    icon: '🏪',
    color: '#4fc3f7',
    ranges: { t3: '0 - 999', t2: '1K - 9.9K', t1: '10K+' }
  },
  {
    value: 'medium',
    label: 'Media',
    icon: '🏢',
    color: '#ff9800',
    ranges: { t3: '0 - 9.9K', t2: '10K - 99K', t1: '100K+' }
  },
  {
    value: 'large',
    label: 'Grande',
    icon: '🏛️',
    color: '#ab47bc',
    ranges: { t3: '0 - 99K', t2: '100K - 999K', t1: '1M+' }
  }
];

const TIER_COLORS = {
  1: { bg: '#4caf50', text: '#fff' },
  2: { bg: '#ff9800', text: '#fff' },
  3: { bg: '#9e9e9e', text: '#fff' }
};

// ─── BrandCard ───────────────────────────────────────────────────────────────
const BrandCard = ({ brand, stats, onUpdate, onDelete, onSetDefault, availableCategories }) => {
  const [expanded, setExpanded] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState(brand.name);
  const [saving, setSaving] = useState(false);
  const [localCategories, setLocalCategories] = useState(brand.relevantCategories || []);
  const [newsletterBanner, setNewsletterBanner] = useState(brand.newsletterBanner || '');
  const [newsletterFooter, setNewsletterFooter] = useState(brand.newsletterFooter || '');
  const [overrides, setOverrides] = useState([]);
  const [loadingOverrides, setLoadingOverrides] = useState(false);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [editingOverride, setEditingOverride] = useState(null);
  const [overrideForm, setOverrideForm] = useState({
    type: 'domain',
    domain: '',
    username: '',
    platform: 'instagram',
    tier: 2,
    reason: '',
    categories: []
  });

  const getOverrideCategories = (override) => {
    const categories = Array.isArray(override?.categories)
      ? override.categories.filter(Boolean)
      : [];

    if (categories.length > 0) return categories;
    return override?.category ? [override.category] : [];
  };

  useEffect(() => { setLocalCategories(brand.relevantCategories || []); }, [brand.relevantCategories]);

  useEffect(() => {
    if (!expanded) return;
    const fetchOverrides = async () => {
      setLoadingOverrides(true);
      try {
        const res = await api.get('/tiers/overrides', { headers: { 'x-brand-id': brand._id } });
        if (res.data.success) setOverrides(res.data.data);
      } catch (err) { console.error('Erro ao carregar overrides:', err); }
      setLoadingOverrides(false);
    };
    fetchOverrides();
  }, [brand._id, expanded]);

  const handleOpenOverrideDialog = (override = null) => {
    if (override) {
      setEditingOverride(override);
      setOverrideForm({
        type: override.domain ? 'domain' : 'username',
        domain: override.domain || '', username: override.username || '',
        platform: override.platform || 'instagram',
        tier: override.tier,
        reason: override.reason || '',
        categories: getOverrideCategories(override)
      });
    } else {
      setEditingOverride(null);
      setOverrideForm({
        type: 'domain',
        domain: '',
        username: '',
        platform: 'instagram',
        tier: 2,
        reason: '',
        categories: []
      });
    }
    setOverrideDialogOpen(true);
  };

  const handleSaveOverride = async () => {
    setSaving(true);
    try {
      const payload = {
        tier: overrideForm.tier,
        reason: overrideForm.reason,
        categories: Array.isArray(overrideForm.categories) ? overrideForm.categories : []
      };
      if (overrideForm.type === 'domain') {
        payload.domain = overrideForm.domain.toLowerCase().replace('www.', '');
      } else {
        payload.username = overrideForm.username.replace('@', '');
        payload.platform = overrideForm.platform;
      }
      if (editingOverride) {
        await api.put(`/tiers/overrides/${editingOverride._id}`, payload, { headers: { 'x-brand-id': brand._id } });
      } else {
        await api.post('/tiers/overrides', payload, { headers: { 'x-brand-id': brand._id } });
      }
      const res = await api.get('/tiers/overrides', { headers: { 'x-brand-id': brand._id } });
      if (res.data.success) setOverrides(res.data.data);
      setOverrideDialogOpen(false);
    } catch (err) { console.error('Erro ao salvar override:', err); }
    setSaving(false);
  };

  const handleOverrideCategoriesChange = (event, newValue) => {
    setOverrideForm(prev => ({
      ...prev,
      categories: Array.isArray(newValue) ? newValue.filter(Boolean) : []
    }));
  };

  const handleDeleteOverride = async (overrideId) => {
    if (!window.confirm('Excluir este override?')) return;
    try {
      await api.delete(`/tiers/overrides/${overrideId}`, { headers: { 'x-brand-id': brand._id } });
      setOverrides(prev => prev.filter(o => o._id !== overrideId));
    } catch (err) { console.error('Erro ao deletar override:', err); }
  };

  const handleSaveName = async () => {
    if (!newName.trim() || newName === brand.name) { setEditingName(false); setNewName(brand.name); return; }
    setSaving(true);
    try {
      await api.put(`/brands/${brand._id}`, { name: newName });
      onUpdate();
      setEditingName(false);
    } catch (err) { console.error('Erro ao salvar nome:', err); setNewName(brand.name); }
    setSaving(false);
  };

  const handleTierProfileChange = async (event, newProfile) => {
    if (!newProfile || newProfile === brand.tierProfile) return;
    setSaving(true);
    try {
      await api.put(`/brands/${brand._id}`, { tierProfile: newProfile });
      onUpdate();
    } catch (err) { console.error('Erro ao salvar perfil:', err); }
    setSaving(false);
  };

  const handleToggleCategory = async (categoryValue) => {
    const newCategories = localCategories.includes(categoryValue)
      ? localCategories.filter(c => c !== categoryValue)
      : [...localCategories, categoryValue];
    setLocalCategories(newCategories);
    try {
      await api.put(`/brands/${brand._id}/categories`, { categories: newCategories });
      onUpdate();
    } catch (err) { console.error('Erro ao salvar categorias:', err); setLocalCategories(brand.relevantCategories || []); }
  };

  const handleSaveNewsletterSettings = async () => {
    setSaving(true);
    try {
      await api.put(`/brands/${brand._id}`, { newsletterBanner, newsletterFooter });
      onUpdate();
    } catch (err) { console.error('Erro ao salvar config de newsletter:', err); }
    setSaving(false);
  };

  const currentProfile = TIER_PROFILES.find(p => p.value === brand.tierProfile) || TIER_PROFILES[1];

  return (
    <Card sx={{
      mb: 1.5,
      border: brand.isDefault ? '2px solid' : '1px solid',
      borderColor: brand.isDefault ? 'primary.main' : 'divider',
      position: 'relative', overflow: 'visible'
    }}>
      {brand.isDefault && (
        <Chip icon={<StarIcon sx={{ fontSize: 14 }} />} label="Padrão" size="small" color="primary"
          sx={{ position: 'absolute', top: -10, right: 16, fontSize: '0.7rem', height: 22 }} />
      )}

      <CardContent sx={{ pb: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: expanded ? 2 : 0 }}>
          {brand.logo ? (
            <Avatar src={brand.logo} sx={{ width: 40, height: 40 }} />
          ) : (
            <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontSize: 20 }}>
              {currentProfile.icon}
            </Avatar>
          )}

          {/* Nome editável */}
          <Box sx={{ flex: 1 }}>
            {editingName ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField value={newName} onChange={(e) => setNewName(e.target.value)} size="small" autoFocus disabled={saving}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') { setEditingName(false); setNewName(brand.name); } }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleSaveName} disabled={saving}><CheckIcon fontSize="small" color="success" /></IconButton>
                        <IconButton size="small" onClick={() => { setEditingName(false); setNewName(brand.name); }}><CloseIcon fontSize="small" /></IconButton>
                      </InputAdornment>
                    )
                  }} sx={{ width: 250 }} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>{brand.name}</Typography>
                <IconButton size="small" onClick={() => setEditingName(true)}>
                  <EditIcon fontSize="small" sx={{ opacity: 0.5 }} />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Stats */}
          <Box sx={{ display: 'flex', gap: 2, mr: 1 }}>
            <Tooltip title="Artigos"><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ArticleIcon sx={{ fontSize: 16, opacity: 0.6 }} />
              <Typography variant="caption" fontWeight={500}>{stats?.articles || 0}</Typography>
            </Box></Tooltip>
            <Tooltip title="Tags"><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TagIcon sx={{ fontSize: 16, opacity: 0.6 }} />
              <Typography variant="caption" fontWeight={500}>{stats?.tags || 0}</Typography>
            </Box></Tooltip>
            <Tooltip title="Coleções"><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CollectionIcon sx={{ fontSize: 16, opacity: 0.6 }} />
              <Typography variant="caption" fontWeight={500}>{stats?.collections || 0}</Typography>
            </Box></Tooltip>
          </Box>

          {/* Ações */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {!brand.isDefault && (
              <Tooltip title="Definir como padrão">
                <IconButton size="small" onClick={() => onSetDefault(brand._id)}><StarBorderIcon /></IconButton>
              </Tooltip>
            )}
            <Tooltip title={brand.isDefault ? 'Não pode excluir marca padrão' : 'Excluir marca'}>
              <span>
                <IconButton size="small" onClick={() => onDelete(brand)} disabled={brand.isDefault} color="error">
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Divider sx={{ my: 2 }} />

          {/* Perfil de Tier */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <TierIcon sx={{ fontSize: 20, color: 'primary.main' }} />
              <Typography variant="subtitle2" fontWeight={600}>Perfil de Tier</Typography>
              <Typography variant="caption" color="text.secondary">(define os limites de seguidores para cada tier)</Typography>
            </Box>
            <ToggleButtonGroup value={brand.tierProfile || 'medium'} exclusive onChange={handleTierProfileChange} disabled={saving} sx={{ width: '100%' }}>
              {TIER_PROFILES.map((profile) => (
                <ToggleButton key={profile.value} value={profile.value} sx={{
                  flex: 1, py: 1.5, flexDirection: 'column', gap: 0.5,
                  '&.Mui-selected': { bgcolor: `${profile.color}22`, borderColor: profile.color, '&:hover': { bgcolor: `${profile.color}33` } }
                }}>
                  <Typography sx={{ fontSize: 24 }}>{profile.icon}</Typography>
                  <Typography variant="body2" fontWeight={600}>{profile.label}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip label={`T3: ${profile.ranges.t3}`} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                    <Chip label={`T2: ${profile.ranges.t2}`} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                    <Chip label={`T1: ${profile.ranges.t1}`} size="small" color="success" sx={{ fontSize: '0.65rem', height: 20 }} />
                  </Box>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Categorias Relevantes */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <CategoryIcon sx={{ fontSize: 20, color: 'primary.main' }} />
              <Typography variant="subtitle2" fontWeight={600}>Categorias Relevantes</Typography>
              <Typography variant="caption" color="text.secondary">(fontes nestas categorias recebem Tier 1 automaticamente)</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {availableCategories.map((category, idx) => {
                const categoryValue = typeof category === 'string' ? category : category?.value;
                const categoryLabel = typeof category === 'string' ? category : (category?.label || category?.value);
                if (!categoryValue) return null;
                const isSelected = localCategories.includes(categoryValue);
                return (
                  <Chip key={categoryValue || idx} label={categoryLabel} size="small"
                    onClick={() => handleToggleCategory(categoryValue)}
                    color={isSelected ? 'primary' : 'default'} variant={isSelected ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer', opacity: isSelected ? 1 : 0.7, '&:hover': { opacity: 1 } }} />
                );
              })}
            </Box>
            {localCategories.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Nenhuma categoria selecionada.
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Newsletter */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <EmailIcon sx={{ fontSize: 20, color: 'primary.main' }} />
              <Typography variant="subtitle2" fontWeight={600}>Configurações da Newsletter</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="URL da Imagem do Banner (Topo)" size="small" value={newsletterBanner}
                onChange={e => setNewsletterBanner(e.target.value)} fullWidth placeholder="https://exemplo.com/banner.jpg" />
              <TextField label="Link de Destino / Link do Rodapé" size="small" value={newsletterFooter}
                onChange={e => setNewsletterFooter(e.target.value)} fullWidth placeholder="https://exemplo.com/contato" />
              <Box>
                <Button variant="outlined" size="small" onClick={handleSaveNewsletterSettings} disabled={saving}>
                  {saving ? <CircularProgress size={20} /> : 'Salvar Configurações da Newsletter'}
                </Button>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Overrides de Tier */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <OverrideIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="subtitle2" fontWeight={600}>Overrides de Tier</Typography>
                <Typography variant="caption" color="text.secondary">(regras manuais que sobrescrevem o cálculo automático)</Typography>
              </Box>
              <Button size="small" startIcon={<AddIcon />} onClick={() => handleOpenOverrideDialog()}>Adicionar</Button>
            </Box>
            {loadingOverrides ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
            ) : overrides.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">Nenhum override configurado.</Typography>
              </Paper>
            ) : (
              <Paper variant="outlined">
                <Table size="small">
                  <TableBody>
                    {overrides.map((override) => (
                      <TableRow key={override._id} hover>
                        <TableCell sx={{ width: 40 }}>
                          {override.domain ? <DomainIcon sx={{ fontSize: 18, opacity: 0.6 }} /> : <UserIcon sx={{ fontSize: 18, opacity: 0.6 }} />}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{override.domain || `@${override.username}`}</Typography>
                          {override.platform && !override.domain && <Typography variant="caption" color="text.secondary">{override.platform}</Typography>}
                        </TableCell>
                        <TableCell align="center" sx={{ width: 60 }}>
                          <Chip label={`T${override.tier}`} size="small"
                            sx={{ bgcolor: TIER_COLORS[override.tier]?.bg, color: TIER_COLORS[override.tier]?.text, fontWeight: 600, minWidth: 40 }} />
                        </TableCell>
                        <TableCell align="right" sx={{ width: 80 }}>
                          <IconButton size="small" onClick={() => handleOpenOverrideDialog(override)}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => handleDeleteOverride(override._id)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </Box>
        </Collapse>
      </CardContent>

      {/* Dialog Override */}
      <PlatformDialog open={overrideDialogOpen} onClose={() => setOverrideDialogOpen(false)} maxWidth="xs" fullWidth>
        <PlatformDialogTitle 
          title={editingOverride ? 'Editar Override' : 'Novo Override de Tier'}
          subtitle={editingOverride ? 'Ajuste a regra de tier manual.' : 'Defina um tier fixo para uma fonte específica.'}
          icon={<OverrideIcon />}
          onClose={() => setOverrideDialogOpen(false)}
        />
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {!editingOverride && (
              <ToggleButtonGroup value={overrideForm.type} exclusive
                onChange={(e, val) => val && setOverrideForm({ ...overrideForm, type: val })} fullWidth size="small">
                <ToggleButton value="domain" sx={{ borderRadius: 2 }}><DomainIcon sx={{ mr: 1 }} />Domínio</ToggleButton>
                <ToggleButton value="username" sx={{ borderRadius: 2 }}><UserIcon sx={{ mr: 1 }} />Perfil</ToggleButton>
              </ToggleButtonGroup>
            )}
            {overrideForm.type === 'domain' ? (
              <TextField 
                label="Domínio Principal" 
                size="small"
                value={overrideForm.domain}
                onChange={(e) => setOverrideForm({ ...overrideForm, domain: e.target.value })}
                fullWidth 
                placeholder="exemplo.com.br" 
                disabled={!!editingOverride}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            ) : (
              <>
                <TextField 
                  label="Username / Identificador" 
                  size="small"
                  value={overrideForm.username}
                  onChange={(e) => setOverrideForm({ ...overrideForm, username: e.target.value })}
                  fullWidth 
                  placeholder="@usuario" 
                  disabled={!!editingOverride}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <FormControl fullWidth size="small" disabled={!!editingOverride}>
                  <InputLabel>Plataforma Alvo</InputLabel>
                  <Select 
                    value={overrideForm.platform} 
                    label="Plataforma Alvo"
                    onChange={(e) => setOverrideForm({ ...overrideForm, platform: e.target.value })}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="instagram">Instagram</MenuItem>
                    <MenuItem value="youtube">YouTube</MenuItem>
                    <MenuItem value="tiktok">TikTok</MenuItem>
                    <MenuItem value="twitter">Twitter/X</MenuItem>
                    <MenuItem value="facebook">Facebook</MenuItem>
                    <MenuItem value="linkedin">LinkedIn</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
            <FormControl fullWidth size="small">
              <InputLabel>Tier de Destino</InputLabel>
              <Select 
                value={overrideForm.tier} 
                label="Tier de Destino" 
                onChange={(e) => setOverrideForm({ ...overrideForm, tier: e.target.value })}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Chip label="T1" size="small" sx={{ bgcolor: TIER_COLORS[1].bg, color: TIER_COLORS[1].text, fontWeight: 700 }} />
                    <Typography variant="body2" fontWeight={500}>Nacional / Premium</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Chip label="T2" size="small" sx={{ bgcolor: TIER_COLORS[2].bg, color: TIER_COLORS[2].text, fontWeight: 700 }} />
                    <Typography variant="body2" fontWeight={500}>Regional / Médio</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Chip label="T3" size="small" sx={{ bgcolor: TIER_COLORS[3].bg, color: TIER_COLORS[3].text, fontWeight: 700 }} />
                    <Typography variant="body2" fontWeight={500}>Local / Nicho</Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            <TextField 
              label="Observações / Motivo" 
              size="small"
              value={overrideForm.reason}
              onChange={(e) => setOverrideForm({ ...overrideForm, reason: e.target.value })}
              fullWidth 
              multiline 
              rows={2} 
              placeholder="Ex: Parceiro estratégico oficial..." 
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Autocomplete
              multiple
              options={availableCategories.map((cat) => cat.value || cat.label || cat)}
              value={overrideForm.categories}
              onChange={handleOverrideCategoriesChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Categorias do override"
                  placeholder="Selecione uma ou mais categorias"
                  size="small"
                  helperText="A principal vai para `category`; o restante fica em `categories`."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                    variant="outlined"
                  />
                ))
              }
              isOptionEqualToValue={(option, value) => option === value}
              filterSelectedOptions
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setOverrideDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancelar</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveOverride}
            disabled={saving || (overrideForm.type === 'domain' ? !overrideForm.domain : !overrideForm.username)}
            sx={{ fontWeight: 700, borderRadius: 2, px: 3 }}
          >
            {saving ? <CircularProgress size={20} /> : (editingOverride ? 'Salvar Regra' : 'Criar Override')}
          </Button>
        </DialogActions>
      </PlatformDialog>
    </Card>
  );
};

// ─── UserBrandsAccordion ─────────────────────────────────────────────────────
const UserBrandsAccordion = ({ userGroup, onUpdate, onDelete, onSetDefault, availableCategories, brandStats }) => {
  const [open, setOpen] = useState(true);

  return (
    <Accordion expanded={open} onChange={() => setOpen(!open)} sx={{ mb: 2, '&:before': { display: 'none' } }} variant="outlined">
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'action.hover' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.dark', fontSize: 16 }}>
            {userGroup.user.name?.charAt(0)?.toUpperCase() || '?'}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>{userGroup.user.name || 'Usuário desconhecido'}</Typography>
            <Typography variant="caption" color="text.secondary">{userGroup.user.email}</Typography>
          </Box>
          <Box sx={{ ml: 'auto', mr: 2 }}>
            <Chip
              icon={<BrandIcon sx={{ fontSize: 14 }} />}
              label={`${userGroup.brands.length} marca${userGroup.brands.length !== 1 ? 's' : ''}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 2 }}>
        {userGroup.brands.map(brand => (
          <BrandCard
            key={brand._id}
            brand={brand}
            stats={brandStats[brand._id]}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onSetDefault={onSetDefault}
            availableCategories={availableCategories}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

// ─── BrandManagement (Main) ──────────────────────────────────────────────────
const BrandManagement = () => {
  const [groupedBrands, setGroupedBrands] = useState([]);   // [{ user, brands }]
  const [allBrands, setAllBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandStats, setBrandStats] = useState({});
  const [availableCategories, setAvailableCategories] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    tierProfile: 'medium',
    userId: ''  // usuário alvo para o admin
  });

  const fetchBrands = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/brands/all');
      if (res.data.success) {
        setAllBrands(res.data.data);
        // Converter grouped object em array ordenado por nome
        const grouped = Object.values(res.data.grouped || {})
          .sort((a, b) => (a.user.name || '').localeCompare(b.user.name || ''));
        setGroupedBrands(grouped);
      }
    } catch (err) {
      console.error('Erro ao carregar marcas:', err);
      setError('Erro ao carregar marcas');
    }
    setLoading(false);
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/users/all');
      if (res.data.success) setAllUsers(res.data.data);
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
    }
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get('/brands/categories');
        if (res.data.success) setAvailableCategories(res.data.data);
      } catch (err) { console.error('Erro ao carregar categorias:', err); }
    };
    fetchBrands();
    fetchUsers();
    fetchCategories();
  }, [fetchBrands, fetchUsers]);

  // Buscar stats das marcas
  useEffect(() => {
    if (allBrands.length === 0) return;
    const loadStats = async () => {
      const stats = {};
      for (const brand of allBrands) {
        try {
          const res = await api.get(`/brands/${brand._id}/stats`);
          if (res.data.success) stats[brand._id] = res.data.data.stats;
        } catch (err) { /* silencioso */ }
      }
      setBrandStats(stats);
    };
    loadStats();
  }, [allBrands]);

  const handleSave = async () => {
    if (!formData.name.trim()) { setError('Nome da marca é obrigatório'); return; }
    if (!formData.userId) { setError('Selecione o usuário/cliente'); return; }

    try {
      await api.post('/brands', {
        name: formData.name.trim(),
        logo: formData.logo || undefined,
        tierProfile: formData.tierProfile,
        userId: formData.userId
      });
      setSuccess('Marca criada com sucesso!');
      setDialogOpen(false);
      setFormData({ name: '', logo: '', tierProfile: 'medium', userId: '' });
      fetchBrands();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar marca');
    }
  };

  const handleDelete = async () => {
    if (!selectedBrand) return;
    try {
      await api.delete(`/brands/${selectedBrand._id}`);
      setSuccess('Marca excluída!');
      setDeleteDialogOpen(false);
      setSelectedBrand(null);
      fetchBrands();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao excluir marca');
      setDeleteDialogOpen(false);
    }
  };

  const handleSetDefault = async (brandId) => {
    try {
      await api.put(`/brands/${brandId}/set-default`);
      setSuccess('Marca definida como padrão!');
      fetchBrands();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao definir padrão');
    }
  };

  const openDeleteDialog = (brand) => { setSelectedBrand(brand); setDeleteDialogOpen(true); };

  const totalBrands = allBrands.length;
  const totalUsers = groupedBrands.length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Gerenciamento de Marcas</Typography>
          <Typography variant="body2" color="text.secondary">
            {loading ? 'Carregando...' : `${totalBrands} marca${totalBrands !== 1 ? 's' : ''} em ${totalUsers} cliente${totalUsers !== 1 ? 's' : ''}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar">
            <IconButton onClick={fetchBrands} disabled={loading}><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Nova Marca
          </Button>
        </Box>
      </Box>

      {/* Alertas */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Conteúdo */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : groupedBrands.length === 0 ? (
        <Card><CardContent sx={{ textAlign: 'center', py: 6 }}>
          <GroupIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
          <Typography color="text.secondary">Nenhuma marca cadastrada no sistema</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)} sx={{ mt: 2 }}>
            Criar primeira marca
          </Button>
        </CardContent></Card>
      ) : (
        groupedBrands.map(userGroup => (
          <UserBrandsAccordion
            key={userGroup.user._id}
            userGroup={userGroup}
            onUpdate={fetchBrands}
            onDelete={openDeleteDialog}
            onSetDefault={handleSetDefault}
            availableCategories={availableCategories}
            brandStats={brandStats}
          />
        ))
      )}

      {/* Dialog: Nova Marca */}
      <PlatformDialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <PlatformDialogTitle 
          title="Nova Marca"
          subtitle="Vincule um novo cliente a um perfil de monitoramento."
          icon={<BrandIcon />}
          onClose={() => setDialogOpen(false)}
        />
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Seleção de Usuário */}
            <FormControl fullWidth size="small" required>
              <InputLabel>Usuário / Cliente Vinculado *</InputLabel>
              <Select
                value={formData.userId}
                label="Usuário / Cliente Vinculado *"
                onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                sx={{ borderRadius: 2 }}
              >
                {allUsers.map(user => (
                  <MenuItem key={user._id} value={user._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: 13, bgcolor: 'primary.main' }}>
                        {user.name?.charAt(0)?.toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{user.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                      </Box>
                      <Chip label={user.role} size="small" sx={{ ml: 'auto', fontSize: '0.65rem', height: 18 }} />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Nome Comercial da Marca" 
              size="small"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth 
              required 
              autoFocus={!formData.userId}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="URL do Logo / Ícone" 
              size="small"
              value={formData.logo}
              onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
              fullWidth 
              placeholder="https://..."
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>Perfil de Sensibilidade (Tier)</Typography>
              <ToggleButtonGroup value={formData.tierProfile} exclusive
                onChange={(e, val) => val && setFormData({ ...formData, tierProfile: val })} sx={{ width: '100%' }}>
                {TIER_PROFILES.map((profile) => (
                  <ToggleButton key={profile.value} value={profile.value} sx={{ 
                    flex: 1, py: 1.5, flexDirection: 'column', borderRadius: 2,
                    '&.Mui-selected': { bgcolor: `${profile.color}11`, borderColor: profile.color }
                  }}>
                    <Typography sx={{ fontSize: 20 }}>{profile.icon}</Typography>
                    <Typography variant="caption" fontWeight={600}>{profile.label}</Typography>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => { setDialogOpen(false); setFormData({ name: '', logo: '', tierProfile: 'medium', userId: '' }); }} sx={{ fontWeight: 600 }}>
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            sx={{ fontWeight: 700, borderRadius: 2, px: 4 }}
          >
            Criar Marca
          </Button>
        </DialogActions>
      </PlatformDialog>

      {/* Dialog: Confirmar Exclusão */}
      <PlatformDialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs">
        <PlatformDialogTitle 
          title="Confirmar Exclusão"
          subtitle="Esta ação removerá todos os dados desta marca."
          icon={<WarningIcon />}
          onClose={() => setDeleteDialogOpen(false)}
          error
        />
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" fontWeight={500}>
            Tem certeza que deseja excluir a marca <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>"{selectedBrand?.name}"</Box>?
          </Typography>
          <Alert severity="error" variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
            Todos os artigos, tags e coleções vinculados serão afetados.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancelar</Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDelete}
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            Excluir Permanentemente
          </Button>
        </DialogActions>
      </PlatformDialog>
    </Box>
  );
};

export default BrandManagement;
